import { z } from 'zod';

import { AVAILABLE_AGENT_TOOLS, type AvailableToolType } from './agent-tools';
import { type OffsetPaginationRequestQuery } from './pagination';
import { JSONSchema, PrimaryKeySchema } from './utils';

// LLM provider configuration schema
export const LLMProviderConfigSchema = z.object({
  provider: z
    .enum(['vertexai', 'anthropic', 'openai'])
    .describe('LLM provider to use'),
  model_tier: z
    .enum(['large', 'medium', 'small'])
    .describe('Model size tier (large = best quality, small = fastest)'),
  model: z.string().optional().describe('Explicit model name override'),
  enable_reasoning: z
    .boolean()
    .optional()
    .describe('Enable reasoning/thinking mode if supported by the provider'),
  model_config: z
    .record(z.any())
    .optional()
    .describe('Provider-specific model configuration'),
});

export const LLMConfigSchema = z
  .object({
    thought_llm: LLMProviderConfigSchema.optional().describe(
      'LLM for agent reasoning',
    ),
    fact_extraction_llm: LLMProviderConfigSchema.optional().describe(
      'LLM for fact extraction',
    ),
    planner_llm:
      LLMProviderConfigSchema.optional().describe('LLM for planning'),
  })
  .describe('LLM provider configuration for different agent components');

// Tool configuration schema
export const ToolConfigItemSchema = z.object({
  type: z
    .enum(
      Object.keys(AVAILABLE_AGENT_TOOLS) as [
        AvailableToolType,
        ...AvailableToolType[],
      ],
    )
    .describe('Tool type identifier from the tool registry'),
  config: z.record(z.any()).optional().describe('Tool-specific configuration'),
});

export const ToolConfigSchema = z
  .array(ToolConfigItemSchema)
  .describe('Array of tools available to the agent');

// Execution configuration schema
export const ExecutionConfigSchema = z.object({
  max_actions: z
    .number()
    .int()
    .positive()
    .default(10)
    .describe('Maximum number of actions the agent can take'),
  planning_interval: z
    .number()
    .int()
    .positive()
    .default(4)
    .describe('Number of actions between re-planning'),
  feedback_rounds: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Number of feedback rounds for refinement'),
  timeout_minutes: z
    .number()
    .int()
    .positive()
    .default(30)
    .describe('Maximum execution time in minutes'),
});

export const AgentConfigurationVersionSchema = z.object({
  agentConfigurationVersionId: PrimaryKeySchema,
  agentId: PrimaryKeySchema,
  instructions: z.string(),
  inputSchema: JSONSchema,
  outputSchema: JSONSchema,
  versionNumber: z.number(),
  isCurrent: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // New configuration fields for agent execution system
  systemPrompt: z.string().nullable(),
  llmConfig: LLMConfigSchema,
  toolConfig: ToolConfigSchema,
  executionConfig: ExecutionConfigSchema,
});

export type AgentConfigurationVersion = z.infer<
  typeof AgentConfigurationVersionSchema
>;

export type LLMProviderConfig = z.infer<typeof LLMProviderConfigSchema>;
export type LLMConfig = z.infer<typeof LLMConfigSchema>;
export type ToolConfigItem = z.infer<typeof ToolConfigItemSchema>;
export type ToolConfig = z.infer<typeof ToolConfigSchema>;
export type ExecutionConfig = z.infer<typeof ExecutionConfigSchema>;

// Schema for creating a new agent configuration version
export const CreateAgentConfigurationVersionSchema =
  AgentConfigurationVersionSchema.pick({
    agentId: true,
    instructions: true,
    inputSchema: true,
    outputSchema: true,
    versionNumber: true,
    isCurrent: true,
    systemPrompt: true,
    llmConfig: true,
    toolConfig: true,
    executionConfig: true,
  });

export type CreateAgentConfigurationVersion = z.infer<
  typeof CreateAgentConfigurationVersionSchema
>;

// Schema for updating an agent configuration version
export const UpdateAgentConfigurationVersionSchema = z.object({
  instructions: z.string().optional(),
  inputSchema: JSONSchema.optional(),
  outputSchema: JSONSchema.optional(),
  systemPrompt: z.string().nullable().optional(),
  llmConfig: LLMConfigSchema.optional(),
  toolConfig: ToolConfigSchema.optional(),
  executionConfig: ExecutionConfigSchema.optional(),
  isCurrent: z.boolean().optional(),
});

export type UpdateAgentConfigurationVersion = z.infer<
  typeof UpdateAgentConfigurationVersionSchema
>;

export const ListAgentConfigurationVersionsOptionsSchema = z
  .object({
    agentIds: PrimaryKeySchema.array(),
    agentConfigurationVersionIds: PrimaryKeySchema.array(),
    isCurrent: z.boolean().optional(),
    versionNumbers: z.number().array().optional(),
  })
  .partial();

export type ListAgentConfigurationVersionsOptions = z.infer<
  typeof ListAgentConfigurationVersionsOptionsSchema
> &
  OffsetPaginationRequestQuery<'versionNumber' | 'createdAt' | 'updatedAt'>;
